import React, { useState, useEffect, useRef } from 'react';
import {
  KeyboardDatePicker,
  KeyboardDatePickerProps,
} from '@material-ui/pickers';
import { withStyles } from '@material-ui/core';

import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
import { ExtendMui } from '@material-ui/pickers/typings/extendMui';
import { format, isValid } from 'date-fns';
import { dDaFormat, dVariant } from 'main_consts';

export const EmptyDateMsg = '請填寫這個欄位。';
export const dateErrStr = '起始日期不得大於結束日期。';

interface ICustomProps {
  required?: boolean;
  verifyBeforeChange?: boolean;
  verifiers?: Iverifier[];
}

interface Iverifier {
  func: (value: ParsableDate) => boolean;
  text: string;
}

// html required 無效，因為 input readonly
const StyleDatePicker = withStyles({
  root: {
    '& .MuiInputBase-formControl': {
      padding: 0,
      '& input': {
        padding: '0.675em 0.675em ',
        // fontSize: '1.15em',
      },
      '& input:disabled': {
        padding: '0.675em 0.75em',
      },
    },
  },
})(KeyboardDatePicker);
interface IDatePickerTyping
  extends Omit<ExtendMui<KeyboardDatePickerProps>, 'onClose'>,
    ICustomProps {
  onClose?: (date: Date | null) => void;
}
const DatePickerTyping = (props: IDatePickerTyping): JSX.Element => {
  const {
    value,
    onChange,
    onClose,
    error,
    helperText,
    verifiers = [],
    verifyBeforeChange = false,
    required,
    className,
    disabled = false,
    clearable = true,
    format: dateFormat = dDaFormat,
    minDate,
    maxDate,
    ...other
  } = props;
  const [message, setMessage] = useState<string | undefined>();
  const [FirstVerify, setFirstVerify] = useState(verifyBeforeChange);

  const dateRef = useRef(value);
  useEffect(() => {
    dateRef.current = value;
  }, [value]);

  if (required) {
    verifiers.push({ func: (d) => !d, text: EmptyDateMsg });
  }

  useEffect(() => {
    // 始設畫面預設不 verify , 除非 verifyBeforeChange = true
    if (!FirstVerify) {
      setFirstVerify(true);
      setMessage(undefined);
      return;
    }

    // 驗使用者設定之 verifier, 空值也要驗(不能驗 HTML validation)
    // input 為 readonly or hidden 時，不會觸發
    if (verifiers.length > 0) {
      const veriObj = verifiers.find((obj) => obj.func(value));
      if (veriObj) {
        setMessage(veriObj.text);
        return;
      }
    }

    // 先前檢驗都 pass
    setMessage(undefined);
  }, [value, verifyBeforeChange]);

  const errorAttr = error || (message || '').length > 0;
  const helperTextAttr = message || helperText;
  return (
    <div className={className} style={{ width: '100%' }}>
      <StyleDatePicker
        disabled={disabled}
        clearable={clearable}
        value={value}
        style={{ width: '100%' }}
        inputVariant={dVariant}
        {...(errorAttr && { error: errorAttr })}
        {...(helperTextAttr && { helperText: helperTextAttr })}
        format={dateFormat}
        minDate={minDate}
        maxDate={maxDate}
        invalidDateMessage="無效的日期格式"
        okLabel="確認"
        cancelLabel="取消"
        clearLabel="清除"
        todayLabel="今日"
        minDateMessage={`日期不得小於${
          isValid(minDate)
            ? ` ${format(minDate as Date, dateFormat)}`
            : '最小日期'
        }`}
        maxDateMessage={`日期不得大於${
          isValid(maxDate)
            ? ` ${format(maxDate as Date, dateFormat)}`
            : '最大日期'
        }`}
        onChange={(d) => {
          dateRef.current = d;
          if (onChange) {
            onChange(d);
          }
        }}
        onClose={() => {
          if (onClose) {
            onClose(dateRef.current as Date | null);
          }
        }}
        {...other}
      />
    </div>
  );
};

export default DatePickerTyping;
